import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AudioWaveform,
  Play,
  Pause,
  Heart,
  MessageSquare,
  Plus,
  Mic,
  Upload,
  Loader2,
  Star,
  Music2,
  Filter,
  User
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTRUMENTS = [
  { value: "bass", label: "Bajo" },
  { value: "guitar", label: "Guitarra" },
  { value: "drums", label: "Batería" },
  { value: "keys", label: "Teclado" },
  { value: "vocals", label: "Voz" },
  { value: "other", label: "Otro" }
];

const GENRES = ["Rock", "Pop", "Jazz", "Gospel", "R&B", "Funk", "Metal", "Otro"];

const ClipsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  
  const [clips, setClips] = useState([]);
  const [myClips, setMyClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("explore");
  
  // Filters
  const [filterInstrument, setFilterInstrument] = useState("all");
  const [filterGenre, setFilterGenre] = useState("all");
  
  // Create clip
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClip, setNewClip] = useState({
    title: "",
    description: "",
    instrument: "bass",
    genre: "Rock",
    key: "",
    bpm: "",
    tags: ""
  });
  const [clipAudioFile, setClipAudioFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  
  // Feedback dialog
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackClip, setFeedbackClip] = useState(null);
  const [feedback, setFeedback] = useState({ timing: 0, groove: 0, tone: 0, creativity: 0, comment: "" });
  
  // Playing
  const [playingClipId, setPlayingClipId] = useState(null);
  const audioRef = useRef(null);

  const fetchClips = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterInstrument !== "all") params.append("instrument", filterInstrument);
      if (filterGenre !== "all") params.append("genre", filterGenre);
      
      const [publicRes, myRes] = await Promise.all([
        axios.get(`${API_URL}/clips?${params.toString()}`),
        axios.get(`${API_URL}/clips/my`, { headers: getAuthHeaders() })
      ]);
      
      setClips(publicRes.data);
      setMyClips(myRes.data);
    } catch (error) {
      console.error("Error fetching clips:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filterInstrument, filterGenre]);

  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  // Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordingBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      // Auto-stop after 60 seconds (clip limit)
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
          setIsRecording(false);
          toast.info("Límite de 60 segundos alcanzado");
        }
      }, 60000);
    } catch (error) {
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Create clip
  const handleCreateClip = async () => {
    if (!newClip.title.trim()) {
      toast.error("Ingresa un título");
      return;
    }
    
    const audioToUpload = clipAudioFile || recordingBlob;
    if (!audioToUpload) {
      toast.error("Graba o sube un audio");
      return;
    }
    
    setCreating(true);
    try {
      // Create clip metadata
      const clipRes = await axios.post(`${API_URL}/clips`, {
        title: newClip.title,
        description: newClip.description || null,
        duration_seconds: 30, // Approximate
        instrument: newClip.instrument,
        genre: newClip.genre,
        key: newClip.key || null,
        bpm: newClip.bpm ? parseInt(newClip.bpm) : null,
        tags: newClip.tags.split(",").map(t => t.trim()).filter(Boolean)
      }, { headers: getAuthHeaders() });
      
      // Upload audio
      const formData = new FormData();
      formData.append("file", audioToUpload, "clip.webm");
      
      await axios.post(
        `${API_URL}/clips/${clipRes.data.id}/upload`,
        formData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Clip publicado!");
      setShowCreateDialog(false);
      resetCreateForm();
      fetchClips();
    } catch (error) {
      toast.error("Error al crear clip");
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setNewClip({ title: "", description: "", instrument: "bass", genre: "Rock", key: "", bpm: "", tags: "" });
    setClipAudioFile(null);
    setRecordingBlob(null);
  };

  // Like
  const handleLike = async (clipId) => {
    try {
      const res = await axios.post(`${API_URL}/clips/${clipId}/like`, {}, { headers: getAuthHeaders() });
      
      // Update local state
      setClips(prev => prev.map(c => 
        c.id === clipId ? { ...c, likes_count: res.data.likes_count } : c
      ));
      setMyClips(prev => prev.map(c => 
        c.id === clipId ? { ...c, likes_count: res.data.likes_count } : c
      ));
    } catch (error) {
      toast.error("Error");
    }
  };

  // Feedback
  const openFeedbackDialog = (clip) => {
    setFeedbackClip(clip);
    setFeedback({ timing: 0, groove: 0, tone: 0, creativity: 0, comment: "" });
    setShowFeedbackDialog(true);
  };

  const submitFeedback = async () => {
    if (!feedbackClip) return;
    
    try {
      await axios.post(`${API_URL}/clips/${feedbackClip.id}/feedback`, feedback, { headers: getAuthHeaders() });
      toast.success("Feedback enviado!");
      setShowFeedbackDialog(false);
      fetchClips();
    } catch (error) {
      toast.error("Error al enviar feedback");
    }
  };

  // Play audio
  const playClip = (clip) => {
    if (playingClipId === clip.id) {
      audioRef.current?.pause();
      setPlayingClipId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = `${API_URL}/clips/${clip.id}/audio`;
        audioRef.current.play();
      }
      setPlayingClipId(clip.id);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-300">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className={`p-1 ${value >= star ? "text-yellow-400" : "text-gray-600"}`}
          >
            <Star className="w-5 h-5" fill={value >= star ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
    </div>
  );

  const ClipCard = ({ clip }) => (
    <Card className="bg-[#18181b] border-white/10 overflow-hidden">
      <CardContent className="p-0">
        {/* Player area */}
        <div 
          className="relative h-32 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center cursor-pointer"
          onClick={() => playClip(clip)}
        >
          <Button
            size="lg"
            className={`h-14 w-14 rounded-full ${playingClipId === clip.id ? "bg-white text-black" : "bg-black/50 text-white"}`}
          >
            {playingClipId === clip.id ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
          </Button>
          
          <div className="absolute top-2 right-2">
            <Badge variant="outline" className="bg-black/50 border-0 text-white text-xs">
              {INSTRUMENTS.find(i => i.value === clip.instrument)?.label || clip.instrument}
            </Badge>
          </div>
        </div>
        
        {/* Info */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-white truncate">{clip.title}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User className="w-3 h-3" />
                <span>{clip.user_name}</span>
              </div>
            </div>
            {clip.avg_rating > 0 && (
              <div className="flex items-center gap-1 text-yellow-400">
                <Star className="w-4 h-4" fill="currentColor" />
                <span className="text-sm">{clip.avg_rating}</span>
              </div>
            )}
          </div>
          
          {clip.description && (
            <p className="text-sm text-gray-400 mb-3 line-clamp-2">{clip.description}</p>
          )}
          
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {clip.key && <span>{clip.key}</span>}
            {clip.bpm && <span>{clip.bpm} BPM</span>}
            {clip.genre && <Badge variant="outline" className="text-xs border-white/10">{clip.genre}</Badge>}
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/5">
            <button 
              onClick={() => handleLike(clip.id)}
              className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors"
            >
              <Heart className="w-4 h-4" />
              <span className="text-sm">{clip.likes_count}</span>
            </button>
            <button 
              onClick={() => openFeedbackDialog(clip)}
              className="flex items-center gap-2 text-gray-400 hover:text-primary transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm">{clip.feedback_count}</span>
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Audio element for playback */}
      <audio ref={audioRef} onEnded={() => setPlayingClipId(null)} className="hidden" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <AudioWaveform className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white font-['Manrope']">SteelMora</span>
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-white font-medium">Clips</span>
          </div>
          
          <Button onClick={() => setShowCreateDialog(true)} className="bg-primary gap-2" data-testid="create-clip-btn">
            <Plus className="w-4 h-4" />
            Nuevo Clip
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6">
            <TabsList className="bg-black/30">
              <TabsTrigger value="explore" className="data-[state=active]:bg-primary">
                Explorar
              </TabsTrigger>
              <TabsTrigger value="mine" className="data-[state=active]:bg-primary">
                Mis Clips
              </TabsTrigger>
            </TabsList>
            
            {activeTab === "explore" && (
              <div className="flex items-center gap-2">
                <Select value={filterInstrument} onValueChange={setFilterInstrument}>
                  <SelectTrigger className="w-32 bg-[#18181b] border-white/10 text-white">
                    <SelectValue placeholder="Instrumento" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-white/10">
                    <SelectItem value="all" className="text-white">Todos</SelectItem>
                    {INSTRUMENTS.map(i => (
                      <SelectItem key={i.value} value={i.value} className="text-white">{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterGenre} onValueChange={setFilterGenre}>
                  <SelectTrigger className="w-28 bg-[#18181b] border-white/10 text-white">
                    <SelectValue placeholder="Género" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-white/10">
                    <SelectItem value="all" className="text-white">Todos</SelectItem>
                    {GENRES.map(g => (
                      <SelectItem key={g} value={g} className="text-white">{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="explore">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : clips.length === 0 ? (
              <Card className="bg-[#18181b] border-white/10 border-dashed">
                <CardContent className="py-16 text-center">
                  <Music2 className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No hay clips aún</h3>
                  <p className="text-gray-400 mb-6">¡Sé el primero en compartir tu música!</p>
                  <Button onClick={() => setShowCreateDialog(true)} className="bg-primary gap-2">
                    <Plus className="w-4 h-4" />
                    Crear Clip
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clips.map(clip => <ClipCard key={clip.id} clip={clip} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mine">
            {myClips.length === 0 ? (
              <Card className="bg-[#18181b] border-white/10 border-dashed">
                <CardContent className="py-16 text-center">
                  <Mic className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4">No has publicado ningún clip</p>
                  <Button onClick={() => setShowCreateDialog(true)} variant="outline" className="border-white/10 text-gray-300 gap-2">
                    <Plus className="w-4 h-4" />
                    Crear mi primer clip
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myClips.map(clip => <ClipCard key={clip.id} clip={clip} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Clip Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetCreateForm(); }}>
        <DialogContent className="bg-[#18181b] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Nuevo Clip Musical</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Audio input */}
            <div className="space-y-3">
              <label className="text-sm text-gray-300">Audio (máx 60 segundos)</label>
              
              {!recordingBlob && !clipAudioFile ? (
                <div className="flex gap-4">
                  <Button
                    variant={isRecording ? "destructive" : "outline"}
                    onClick={isRecording ? stopRecording : startRecording}
                    className={isRecording ? "" : "border-white/10 text-gray-300"}
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    {isRecording ? "Detener" : "Grabar"}
                  </Button>
                  <label className="cursor-pointer">
                    <Button variant="outline" className="border-white/10 text-gray-300" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Subir archivo
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={(e) => setClipAudioFile(e.target.files[0])}
                    />
                  </label>
                </div>
              ) : (
                <div className="flex items-center gap-4 p-3 bg-black/30 rounded-lg">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                    <Music2 className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-white flex-1">
                    {clipAudioFile?.name || "Grabación lista"}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setClipAudioFile(null); setRecordingBlob(null); }}
                    className="text-gray-400"
                  >
                    Cambiar
                  </Button>
                </div>
              )}
            </div>
            
            <Input
              placeholder="Título del clip *"
              value={newClip.title}
              onChange={(e) => setNewClip({ ...newClip, title: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
            />
            
            <Input
              placeholder="Descripción (opcional)"
              value={newClip.description}
              onChange={(e) => setNewClip({ ...newClip, description: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Select value={newClip.instrument} onValueChange={(v) => setNewClip({ ...newClip, instrument: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 text-white">
                  <SelectValue placeholder="Instrumento" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  {INSTRUMENTS.map(i => (
                    <SelectItem key={i.value} value={i.value} className="text-white">{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={newClip.genre} onValueChange={(v) => setNewClip({ ...newClip, genre: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 text-white">
                  <SelectValue placeholder="Género" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  {GENRES.map(g => (
                    <SelectItem key={g} value={g} className="text-white">{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Tonalidad (ej: Am)"
                value={newClip.key}
                onChange={(e) => setNewClip({ ...newClip, key: e.target.value })}
                className="bg-black/30 border-white/10 text-white"
              />
              <Input
                type="number"
                placeholder="BPM"
                value={newClip.bpm}
                onChange={(e) => setNewClip({ ...newClip, bpm: e.target.value })}
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
            
            <Input
              placeholder="Tags (separados por coma)"
              value={newClip.tags}
              onChange={(e) => setNewClip({ ...newClip, tags: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/10 text-gray-300">
              Cancelar
            </Button>
            <Button onClick={handleCreateClip} disabled={creating} className="bg-primary">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="bg-[#18181b] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Dar Feedback</DialogTitle>
          </DialogHeader>
          
          {feedbackClip && (
            <div className="space-y-4 py-4">
              <p className="text-gray-400">Para: <span className="text-white">{feedbackClip.title}</span></p>
              
              <div className="space-y-3">
                <StarRating label="Timing" value={feedback.timing} onChange={(v) => setFeedback({ ...feedback, timing: v })} />
                <StarRating label="Groove" value={feedback.groove} onChange={(v) => setFeedback({ ...feedback, groove: v })} />
                <StarRating label="Tono" value={feedback.tone} onChange={(v) => setFeedback({ ...feedback, tone: v })} />
                <StarRating label="Creatividad" value={feedback.creativity} onChange={(v) => setFeedback({ ...feedback, creativity: v })} />
              </div>
              
              <Input
                placeholder="Comentario (opcional)"
                value={feedback.comment}
                onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackDialog(false)} className="border-white/10 text-gray-300">
              Cancelar
            </Button>
            <Button onClick={submitFeedback} className="bg-primary">
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClipsPage;
